import React, { PureComponent, createContext } from 'react';
import { arrayOf, bool, func, node, number, oneOfType, string } from 'prop-types';
import classnames from 'classnames';

import { Button } from '~/elements';

import styles from './Accordion.scss';

const { Provider, Consumer } = createContext();

export class Accordion extends PureComponent {
  constructor(props) {
    super(props);

    this.isConrolled = Array.isArray(props.activeIndexes) && !!props.onChange;

    const defaultIndexes = props.multiple ? props.defaultIndexes : props.defaultIndexes.slice(0, 1);

    this.state = {
      activeIndexes: this.isConrolled ? [] : defaultIndexes,
    };
  }

  getNextActiveIndexes = (indexesList, index) => {
    const { multiple } = this.props;

    let nextActiveIndexes;
    const isOpen = indexesList.includes(index);

    if (multiple) {
      nextActiveIndexes = isOpen
        ? indexesList.filter((it) => it !== index)
        : indexesList.concat(index);
    } else {
      nextActiveIndexes = isOpen ? indexesList.filter((it) => it !== index) : [index];
    }

    return nextActiveIndexes;
  };

  handleChange = (index) => {
    const { onChange, activeIndexes: activeIndexesProp } = this.props;
    const { activeIndexes: activeIndexesState } = this.state;
    const nextActiveIndexes = this.isConrolled
      ? this.getNextActiveIndexes(activeIndexesProp, index)
      : this.getNextActiveIndexes(activeIndexesState, index);

    this.setState(
      (prevState) => ({
        activeIndexes: this.isConrolled ? prevState.activeIndex : nextActiveIndexes,
      }),
      () => {
        if (onChange) {
          onChange(nextActiveIndexes);
        }
      },
    );
  };

  render() {
    const { children, activeIndexes: activeIndexesProp } = this.props;
    const { activeIndexes: activeIndexesState } = this.state;
    const activeIndexes = this.isConrolled ? activeIndexesProp : activeIndexesState;

    return (
      <Provider
        value={{
          onChange: this.handleChange,
        }}
      >
        <section aria-label="Accordion Control Group Buttons">
          {React.Children.map(
            children,
            (child, index) =>
              !!child &&
              React.cloneElement(child, {
                index,
                isOpened: activeIndexes.includes(index),
              }),
          )}
        </section>
      </Provider>
    );
  }
}

Accordion.propTypes = {
  children: oneOfType([node, arrayOf(node)]),
  multiple: bool,
  defaultIndexes: arrayOf(number),
  activeIndexes: arrayOf(number),
  onChange: func,
};

Accordion.defaultProps = {
  multiple: false,
  defaultIndexes: [],
  activeIndexes: undefined,
  children: null,
  onChange: undefined,
};

export const AccordionItem = ({
  children = null,
  index,
  isOpened = false,
  className,
  ...props
}) => (
  <div className={classnames(styles.AccordionItem, className)} {...props}>
    {React.Children.map(
      children,
      (child) =>
        child &&
        React.cloneElement(child, {
          index,
          isOpened,
        }),
    )}
  </div>
);

AccordionItem.propTypes = {
  children: oneOfType([node, arrayOf(node)]),
  index: number,
  isOpened: bool,
  className: string,
};

export const Opener = ({ isOpened, className, ...props } = {}) => (
  <span
    className={classnames(styles.Opener, className, {
      [styles.Plus]: !isOpened,
      [styles.Minus]: isOpened,
    })}
    {...props}
  />
);

Opener.propTypes = {
  isOpened: bool.isRequired,
  className: string,
};

export const AccordionButton = ({
  children = null,
  index,
  isOpened = false,
  className,
  opener: Icon = Opener,
  ...props
}) => (
  <Consumer>
    {({ onChange }) => (
      <Button
        aria-controls={`content-${index}`}
        aria-expanded={isOpened}
        id={`accordion-control-${index}`}
        onClick={() => onChange(index)}
        className={classnames(styles.AccordionButton, className)}
        {...props}
      >
        {children}
        <Icon isOpened={isOpened} className={styles.AccordionIcon} />
      </Button>
    )}
  </Consumer>
);

AccordionButton.propTypes = {
  children: oneOfType([node, arrayOf(node)]),
  className: string,
  index: number,
  isOpened: bool,
  opener: func,
};

export function AccordionPanel({ children = null, isOpened = false, index, ...props }) {
  return (
    <div
      aria-hidden={!isOpened}
      id={`content-${index}`}
      className={classnames(styles.AccordionPanel, {
        [styles.Hidden]: !isOpened,
      })}
      {...props}
    >
      {children}
    </div>
  );
}

AccordionPanel.propTypes = {
  children: oneOfType([node, arrayOf(node)]),
  isOpened: bool,
  index: number,
};
